import { useCallback, useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import api from '../../services/axiosInterceptor.js';
import useApiData from '../../services/useApiData.js';

export function skuOptionLabel(o) {
    if (o == null) return '';
    if (typeof o === 'string') return o;
    const sku = o.sku || o.listingField || '';
    if (!sku) return '';
    const itemId = o.itemId ? ` · ${o.itemId}` : '';
    const category = o.category ? ` (${o.category})` : '';
    return `${sku}${itemId}${category}`;
}

export function skuObjectsFromTree(tree) {
    const list =
        Array.isArray(tree?.skus) && tree.skus.length
            ? tree.skus
            : tree?.sku
              ? [tree.sku]
              : [];
    return list.filter(Boolean).map((sku) =>
        typeof sku === 'string' ? { sku } : sku
    );
}

export function scopePayload({ scopeType, category, skus }) {
    const skuList = (skus || []).map((s) => s?.sku).filter(Boolean);
    const categoryValue =
        typeof category === 'string' ? category.trim() : String(category || '').trim();
    if (scopeType === 'sku') {
        return {
            scopeType: 'sku',
            sku: skuList[0] || '',
            skus: skuList,
            category: skus?.[0]?.category || categoryValue || '',
        };
    }
    return {
        scopeType: 'category',
        category: categoryValue,
        sku: '',
        skus: [],
    };
}

export function canSubmitScope({ scopeType, category, skus }) {
    if (scopeType === 'category') {
        return Boolean(typeof category === 'string' ? category.trim() : category);
    }
    return (skus || []).some((s) => s?.sku);
}

export default function TreeScopeFields({
    scopeType,
    onScopeTypeChange,
    category,
    onCategoryChange,
    skus = [],
    onSkusChange,
    helperText,
}) {
    const [azCategories, setAzCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categoryInput, setCategoryInput] = useState(
        typeof category === 'string' ? category : category ?? ''
    );
    const [skuSearch, setSkuSearch] = useState('');
    const [debouncedSkuSearch, setDebouncedSkuSearch] = useState('');

    const loadCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const { data } = await api.get('/api/broylesllc/inspection-trees/meta/categories');
            setAzCategories(Array.isArray(data) ? data : []);
        } catch {
            setAzCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        setCategoryInput(typeof category === 'string' ? category : category ?? '');
    }, [category]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSkuSearch(skuSearch.trim()), 300);
        return () => clearTimeout(t);
    }, [skuSearch]);

    const canSearchSku = debouncedSkuSearch.length >= 2;
    const skuEndpoint = useMemo(
        () =>
            `api/broylesllc/inspection-trees/meta/skus?search=${encodeURIComponent(debouncedSkuSearch)}&limit=40`,
        [debouncedSkuSearch]
    );
    const { data: skuCatalog, load: loadingSkus } = useApiData(skuEndpoint, {
        enabled: canSearchSku && scopeType === 'sku',
    });

    const skuOptions = useMemo(() => {
        const fromSearch = Array.isArray(skuCatalog) ? skuCatalog : [];
        const merged = [...skus];
        for (const o of fromSearch) {
            const already = merged.some(
                (s) => (s?.id && o.id === s.id) || (s?.sku && o.sku === s.sku)
            );
            if (!already) merged.push(o);
        }
        return merged;
    }, [skuCatalog, skus]);

    const dialogCategoryOptions = useMemo(() => {
        const fromApi = Array.isArray(azCategories) ? azCategories : [];
        if (category && !fromApi.includes(category)) {
            return [category, ...fromApi];
        }
        return fromApi;
    }, [azCategories, category]);

    return (
        <>
            {helperText ? (
                <p className="insp-meta" style={{ marginBottom: 12 }}>
                    {helperText}
                </p>
            ) : null}
            <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={scopeType}
                onChange={(_, v) => v && onScopeTypeChange(v)}
                sx={{ mb: 2 }}
            >
                <ToggleButton value="category">AZ Category</ToggleButton>
                <ToggleButton value="sku">SKU</ToggleButton>
            </ToggleButtonGroup>

            {scopeType === 'category' ? (
                <Autocomplete
                    freeSolo
                    options={dialogCategoryOptions ?? []}
                    loading={loadingCategories}
                    value={category ?? ''}
                    inputValue={categoryInput}
                    onOpen={() => setCategoryInput('')}
                    onClose={() =>
                        setCategoryInput(typeof category === 'string' ? category : category ?? '')
                    }
                    onChange={(_, v) => onCategoryChange(v || null)}
                    onInputChange={(_, v, reason) => {
                        setCategoryInput(String(v ?? ''));
                        if (reason === 'input' || reason === 'clear') {
                            onCategoryChange(String(v ?? '') || null);
                        }
                    }}
                    getOptionLabel={(option) => String(option ?? '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="AZ Category"
                            margin="dense"
                            required
                            helperText={
                                loadingCategories
                                    ? 'Loading categories…'
                                    : dialogCategoryOptions.length
                                      ? 'Open the list to pick a different category, or type a new one'
                                      : 'No options loaded — type a category name'
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingCategories && <CircularProgress size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            ) : (
                <Autocomplete
                    multiple
                    options={skuOptions ?? []}
                    loading={loadingSkus}
                    value={skus}
                    onChange={(_, v) => onSkusChange(Array.isArray(v) ? v : [])}
                    inputValue={skuSearch}
                    onInputChange={(_, v, reason) => {
                        if (reason === 'reset') {
                            setSkuSearch('');
                            return;
                        }
                        setSkuSearch(String(v ?? ''));
                    }}
                    getOptionLabel={skuOptionLabel}
                    isOptionEqualToValue={(a, b) =>
                        Boolean(
                            (a?.id && b?.id && a.id === b.id) ||
                                (a?.sku && b?.sku && a.sku === b.sku)
                        )
                    }
                    filterOptions={(x) => (Array.isArray(x) ? x : [])}
                    noOptionsText={
                        skuSearch.trim().length < 2 && skus.length === 0
                            ? 'Type at least 2 characters'
                            : loadingSkus
                              ? 'Searching…'
                              : 'No SKUs found'
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="SKU(s) — Listing Field"
                            margin="dense"
                            required
                            helperText={
                                skus.length
                                    ? `${skus.length} SKU(s) selected — remove a chip to replace, or search to add more`
                                    : 'Search Products · Listing Field (multi-select)'
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingSkus && <CircularProgress size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            )}
        </>
    );
}
